import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { verifyPasscode, adminLeads, adminBookings, adminGuideOptins, adminDeleteLead } from '../services/api';
import { LogOut, Lock, RefreshCw, Trash2, Mail, Phone, Calendar, BookOpen, Inbox } from 'lucide-react';

const KEY = 'st_admin_passcode';

function Login({ onOk }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyPasscode(code);
      localStorage.setItem(KEY, code);
      onOk(code);
    } catch (err) {
      toast.error('Invalid passcode');
    } finally { setLoading(false); }
  };
  return (
    <main className="min-h-[70vh] grid place-items-center bg-[#0a0a0d] px-5">
      <Card className="bg-[#111116] border-white/10 rounded-none p-8 max-w-sm w-full">
        <div className="w-11 h-11 grid place-items-center brand-gradient"><Lock className="w-5 h-5 text-white" /></div>
        <div className="mt-4 text-[11px] tracking-[0.3em] uppercase text-brand">Restricted</div>
        <h1 className="font-display italic text-3xl uppercase text-white mt-2">Admin Dashboard</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Passcode</label>
            <Input type="password" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
          </div>
          <Button disabled={loading} type="submit" className="w-full brand-gradient text-white rounded-none uppercase tracking-widest text-xs py-6 glow-brand">
            {loading ? 'Verifying...' : 'Enter'}
          </Button>
        </form>
      </Card>
    </main>
  );
}

function Section({ title, count, children, onRefresh }) {
  return (
    <Card className="bg-[#111116] border-white/10 rounded-none">
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="font-display uppercase text-white text-xl">{title}</div>
          <Badge className="bg-brand text-white border-0 rounded-none text-[10px] tracking-widest uppercase">{count}</Badge>
        </div>
        <Button size="sm" variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white hover:text-black rounded-none uppercase text-[11px] tracking-widest" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>
      {children}
    </Card>
  );
}

function LeadsPanel({ passcode }) {
  const [rows, setRows] = useState([]);
  const load = () => adminLeads(passcode).then(setRows).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [passcode]);
  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await adminDeleteLead(passcode, id);
    setRows((r) => r.filter((x) => x.id !== id));
    toast.success('Deleted');
  };
  return (
    <Section title="Leads" count={rows.length} onRefresh={load}>
      <div className="divide-y divide-white/5">
        {rows.length === 0 && <div className="p-10 text-center text-white/50 text-sm">No leads yet. Enquiries from the site land here.</div>}
        {rows.map((l) => (
          <div key={l.id} className="p-5 grid md:grid-cols-6 gap-3 items-start hover:bg-white/[0.02]">
            <div className="md:col-span-2">
              <div className="text-white font-semibold">{l.name}</div>
              <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{l.source}</div>
              <div className="text-xs text-white/40 mt-1">{new Date(l.created_at).toLocaleString()}</div>
            </div>
            <div className="md:col-span-2 text-sm text-white/80 space-y-1">
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand" /> {l.email}</div>
              {l.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-brand" /> {l.phone}</div>}
              {l.goal && <div className="text-xs text-white/50 uppercase tracking-wider">Goal: {l.goal}</div>}
            </div>
            <div className="md:col-span-2 flex items-start justify-between gap-3">
              <div className="text-sm text-white/70 whitespace-pre-wrap">{l.message || <span className="text-white/30">No message</span>}</div>
              <Button size="sm" variant="outline" onClick={() => remove(l.id)} className="bg-transparent border-white/15 text-white hover:bg-brand hover:text-white hover:border-brand rounded-none">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function BookingsPanel({ passcode }) {
  const [rows, setRows] = useState([]);
  const load = () => adminBookings(passcode).then(setRows).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [passcode]);
  return (
    <Section title="Class Bookings" count={rows.length} onRefresh={load}>
      <div className="divide-y divide-white/5">
        {rows.length === 0 && <div className="p-10 text-center text-white/50 text-sm">No bookings yet.</div>}
        {rows.map((b) => (
          <div key={b.id} className="p-5 grid md:grid-cols-5 gap-3 items-center hover:bg-white/[0.02]">
            <div>
              <div className="text-brand font-display text-lg">{b.time}</div>
              <div className="text-xs text-white/50 uppercase tracking-wider">{b.day}</div>
            </div>
            <div className="text-white font-semibold">{b.class_name}</div>
            <div className="text-white/80 text-sm">{b.name}</div>
            <div className="text-white/70 text-sm flex flex-col gap-1">
              <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand" /> {b.email}</span>
              {b.phone && <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-brand" /> {b.phone}</span>}
            </div>
            <div className="text-xs text-white/40">{new Date(b.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function OptinsPanel({ passcode }) {
  const [rows, setRows] = useState([]);
  const load = () => adminGuideOptins(passcode).then(setRows).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [passcode]);
  return (
    <Section title="Guide Optins" count={rows.length} onRefresh={load}>
      <div className="divide-y divide-white/5">
        {rows.length === 0 && <div className="p-10 text-center text-white/50 text-sm">No optins yet.</div>}
        {rows.map((o) => (
          <div key={o.id} className="p-5 grid md:grid-cols-4 gap-3 items-center hover:bg-white/[0.02]">
            <div className="text-white font-semibold">{o.name || <span className="text-white/40">Anonymous</span>}</div>
            <div className="md:col-span-2 text-sm text-white/80 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand" /> {o.email}</div>
            <div className="text-xs text-white/40">{new Date(o.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function AdminDashboard() {
  const [passcode, setPasscode] = useState(localStorage.getItem(KEY) || '');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!passcode) return;
    verifyPasscode(passcode).then(() => setOk(true)).catch(() => { localStorage.removeItem(KEY); setPasscode(''); });
  }, [passcode]);

  if (!ok) return <Login onOk={(c) => { setPasscode(c); setOk(true); }} />;

  const logout = () => { localStorage.removeItem(KEY); setPasscode(''); setOk(false); };

  return (
    <main className="min-h-[80vh] bg-[#0a0a0d] py-14">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Admin</div>
            <h1 className="font-display italic text-white text-4xl md:text-5xl uppercase mt-1">Dashboard</h1>
          </div>
          <Button variant="outline" onClick={logout} className="bg-transparent border-white/15 text-white hover:bg-white hover:text-black rounded-none uppercase text-[11px] tracking-widest">
            <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
          </Button>
        </div>

        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-[#111116] border border-white/10 rounded-none p-1 mb-6">
            <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-widest text-[11px]">
              <Inbox className="w-3.5 h-3.5 mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-widest text-[11px]">
              <Calendar className="w-3.5 h-3.5 mr-2" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="optins" className="rounded-none data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-widest text-[11px]">
              <BookOpen className="w-3.5 h-3.5 mr-2" /> Guide Optins
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><LeadsPanel passcode={passcode} /></TabsContent>
          <TabsContent value="bookings"><BookingsPanel passcode={passcode} /></TabsContent>
          <TabsContent value="optins"><OptinsPanel passcode={passcode} /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
